import { GraphQLEnumType, GraphQLInputObjectType, GraphQLList, GraphQLNonNull, GraphQLScalarType } from "graphql";
import { GraphQLType } from "../classes/graphql-type.class.js";
import { Helper } from "../classes/helper.class.js";
export function useGraphQLMeta(schema) {
  const args = {};
  const fields = {};
  function getMethodNames() {
    return {
      mutation: Object.keys(schema.getMutationType()?.getFields() || {}),
      query: Object.keys(schema.getQueryType()?.getFields() || {}),
      subscription: Object.keys(schema.getSubscriptionType()?.getFields() || {})
    };
  }
  function getTypesForMethod(method, type) {
    let returnType = null;
    let argType = null;
    const customTypes = [];
    const returnDeepType = getDeepType(schema["get" + type + "Type"]()["_fields"][method], {});
    const argsDeepType = getArgs(method);
    if (returnDeepType) {
      returnType = returnDeepType.type + (returnDeepType.isList ? "[]" : "");
      returnType = returnType.replace(/Boolean/g, "boolean");
      if (checkCustomTyp(returnDeepType.type)) {
        customTypes.push(returnDeepType.type);
      }
    }
    if (argsDeepType) {
      const result = [];
      for (const [key, value] of Object.entries(argsDeepType.fields)) {
        result.push(key + (argsDeepType.fields[key].isRequired ? "" : "?") + ": " + argsDeepType.fields[key].type + (argsDeepType.fields[key].isList ? "[]" : ""));
        if (checkCustomTyp(argsDeepType.fields[key].type)) {
          customTypes.push(argsDeepType.fields[key].type);
        }
      }
      argType = result.join(", ");
      argType = argType.replace(/String/g, "string").replace(/Upload/g, "any").replace(/Boolean/g, "boolean").replace(/Int/g, "number").replace(/Float/g, "number");
    }
    if (returnType) {
      returnType = returnType.replace(/Int/g, "number").replace(/Float/g, "number").replace(/Boolean/g, "boolean").replace(/String/g, "string").replace(/Upload/g, "any");
    }
    return { argType, customTypes, returnType };
  }
  function checkCustomTyp(type) {
    const defaultTypes = ["boolean", "string", "date", "int", "number", "float"];
    return !defaultTypes.includes(type.toLocaleLowerCase());
  }
  function parseVariables(variables, fields2, log = false) {
    const result = {};
    if (!variables) {
      return result;
    }
    if (!fields2) {
      return variables;
    }
    if (typeof variables === "object" && Object.keys(fields2)?.length) {
      for (const [key, value] of Object.entries(variables)) {
        if (log) {
          console.debug("key", key, "value", value, "fields");
        }
        if (Array.isArray(value)) {
          result[key] = value.map((item) => parseVariables(item, fields2[key].fields, log));
          continue;
        }
        if (!fields2[key] || !fields2[key]?.type) {
          console.error("GraphQLMeta::parseVariables->fields-> ", key, " is missing type in fields. Please check the schema or input");
          continue;
        }
        if (log) {
          console.debug("type", fields2[key]?.type);
        }
        switch (fields2[key]?.type) {
          case "String":
            result[key] = value;
            break;
          case "Number":
            result[key] = parseFloat(value);
            break;
          case "Float":
            result[key] = parseFloat(value);
            break;
          case "Int":
            result[key] = parseInt(value, 10);
            break;
          case "Boolean":
            result[key] = Boolean(value);
            break;
          case "Date":
            result[key] = new Date(value);
            break;
          default:
            if (typeof value === "object" && Object.keys(fields2[key].fields)?.length) {
              result[key] = parseVariables(value, fields2[key].fields, log);
            } else {
              result[key] = value;
            }
            break;
        }
      }
    } else {
      return variables;
    }
    return result;
  }
  function getRequestTypesViaMethod(methodName) {
    const result = [];
    const methodNames = getMethodNames();
    for (const [key, value] of Object.entries(methodNames)) {
      if (value.includes(methodName)) {
        result.push(key);
      }
    }
    return result;
  }
  function getTypes(logging = false) {
    const possibleTypes = [];
    const mutationType = schema.getMutationType();
    const queryType = schema.getQueryType();
    if (logging) {
      console.debug("GraphQLMeta::getTypes->queryTypeFields", queryType.getFields());
    }
    for (const [key, value] of Object.entries(queryType.getFields())) {
      if (key.startsWith("find")) {
        possibleTypes.push({
          create: false,
          delete: false,
          duplicate: false,
          name: key.split("find").pop().slice(0, -1),
          update: false
        });
      }
    }
    if (logging) {
      console.debug("GraphQLMeta::getTypes->mutationTypeFields", mutationType.getFields());
      console.debug("GraphQLMeta::getTypes->possibleTypes", possibleTypes);
    }
    for (const [key, value] of Object.entries(mutationType.getFields())) {
      if (key.startsWith("create")) {
        const model = key.split("create").pop();
        if (possibleTypes.find((e) => e.name === model)) {
          possibleTypes.find((item) => item.name === model).create = true;
        }
      }
      if (key.startsWith("update")) {
        const model = key.split("update").pop();
        if (possibleTypes.find((e) => e.name === model)) {
          possibleTypes.find((item) => item.name === model).update = true;
        }
      }
      if (key.startsWith("delete")) {
        const model = key.split("delete").pop();
        if (possibleTypes.find((e) => e.name === model)) {
          possibleTypes.find((item) => item.name === model).delete = true;
        }
      }
      if (key.startsWith("duplicate")) {
        const model = key.split("duplicate").pop();
        if (possibleTypes.find((e) => e.name === model)) {
          possibleTypes.find((item) => item.name === model).duplicate = true;
        }
      }
    }
    if (logging) {
      console.debug("GraphQLMeta::getTypes->possibleTypes", possibleTypes);
      console.debug(
        "GraphQLMeta::getTypes->filteredPossibleTypes",
        possibleTypes.filter((e) => e.create || e.update)
      );
    }
    return possibleTypes.filter((e) => e.create || e.update);
  }
  function getArgs(functionName, options = {}) {
    const { cache, freeze, type } = {
      cache: true,
      freeze: true,
      type: void 0,
      ...options
    };
    if (cache && freeze) {
      const argsCache = args[functionName + type];
      if (argsCache) {
        return argsCache;
      }
    }
    const func = getFunction(functionName, { type });
    const result = new GraphQLType();
    if (func?.args) {
      func.args.forEach((item) => {
        result.fields[item.name] = getDeepType(item.type);
      });
    }
    if (freeze) {
      args[functionName + type] = Helper.deepFreeze(result);
    }
    return result;
  }
  function getFields(functionName, options = {}) {
    const { cache, freeze, type } = {
      cache: true,
      freeze: true,
      type: void 0,
      ...options
    };
    if (cache && freeze) {
      const fieldsCache = fields[functionName + type];
      if (fieldsCache) {
        return fieldsCache;
      }
    }
    const func = getFunction(functionName, options);
    const result = getDeepType(func.type);
    if (freeze) {
      fields[functionName + type] = Helper.deepFreeze(result);
    }
    return result;
  }
  function getFunction(name, options = {}) {
    const config = {
      ...options
    };
    let functions = {};
    if (config.type) {
      const graphQLType = config.type.charAt(0).toUpperCase() + config.type.slice(1);
      const type = schema.getType(graphQLType);
      if (type) {
        functions = type.getFields();
      }
    } else {
      ["Subscription", "Mutation", "Query"].forEach((item) => {
        const type = schema.getType(item);
        if (type) {
          functions = {
            ...functions,
            ...type.getFields() || {}
          };
        }
      });
    }
    return functions[name];
  }
  function getTypeName(type) {
    if (type instanceof GraphQLInputObjectType || type instanceof GraphQLScalarType || type instanceof GraphQLEnumType) {
      return type.name;
    } else if (type.type) {
      return getTypeName(type.type);
    } else if (type.ofType) {
      return getTypeName(type.ofType);
    } else {
      return type.name;
    }
  }
  function getDeepType(type, prepared = {}) {
    try {
      if (!type) {
        return new GraphQLType();
      }
      const typeName = getTypeName(type);
      const graphQLType = GraphQLType.map({
        type: getTypeName(type)
      });
      if (typeof type === "object") {
        const preparedType = prepared[typeName];
        if (preparedType) {
          const clone = Object.assign({}, preparedType);
          clone.fields = preparedType.fields;
          if (type.type) {
            if (type.type instanceof GraphQLNonNull) {
              clone.isRequired = true;
              if (type.type.ofType instanceof GraphQLList) {
                clone.isList = true;
                clone.isItemRequired = type.type.ofType.ofType instanceof GraphQLNonNull;
              } else {
                clone.isList = false;
                clone.isItemRequired = false;
              }
            } else {
              clone.isRequired = false;
              if (type.type instanceof GraphQLList) {
                clone.isList = true;
                clone.isItemRequired = type.ofType instanceof GraphQLNonNull;
              } else {
                clone.isList = false;
                clone.isItemRequired = false;
              }
            }
          }
          return clone;
        }
        if (type.name && type.name[0].toUpperCase() === type.name[0] && !prepared[type.name]) {
          prepared[type.name] = graphQLType;
        }
      }
      if (type.ofType) {
        const ofTypeResult = getDeepType(type.ofType, prepared);
        if (type instanceof GraphQLNonNull) {
          ofTypeResult.isRequired = true;
        } else {
          ofTypeResult.isRequired = false;
          if (type instanceof GraphQLList) {
            ofTypeResult.isList = true;
            ofTypeResult.isItemRequired = type.ofType instanceof GraphQLNonNull;
          }
        }
        Object.assign(graphQLType, ofTypeResult);
        return graphQLType;
      }
      if (type._fields) {
        const fields2 = {};
        for (const [key, value] of Object.entries(type._fields)) {
          fields2[key] = getDeepType(value, prepared);
        }
        Object.assign(graphQLType.fields, fields2);
        return graphQLType;
      }
      if (type.type) {
        const typeResult = getDeepType(type.type, prepared);
        if (type.type instanceof GraphQLNonNull) {
          typeResult.isRequired = true;
          if (type.type.ofType instanceof GraphQLList) {
            typeResult.isList = true;
            typeResult.isItemRequired = type.type.ofType.ofType instanceof GraphQLNonNull;
          } else {
            typeResult.isList = false;
            typeResult.isItemRequired = false;
          }
        } else {
          typeResult.isRequired = false;
          if (type.type instanceof GraphQLList) {
            typeResult.isList = true;
            typeResult.isItemRequired = type.ofType instanceof GraphQLNonNull;
          } else {
            typeResult.isList = false;
            typeResult.isItemRequired = false;
          }
        }
        Object.assign(graphQLType, typeResult);
        return graphQLType;
      }
      if (type._values) {
        graphQLType.isEnum = true;
      }
      return graphQLType;
    } catch (e) {
      console.error("GraphQLMeta::getDeepType->error", e);
      return new GraphQLType();
    }
  }
  return {
    getMethodNames,
    getTypesForMethod,
    checkCustomTyp,
    parseVariables,
    getRequestTypesViaMethod,
    getTypes,
    getArgs,
    getFields
  };
}
